import {ActionIcon, Button, Group, Stack, Text, TextInput, Title} from '@mantine/core'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {IconEdit} from '@tabler/icons-react'
import {useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {type CustomerDetail} from 'src/customers/schemas'
import {useUpdateCustomer} from 'src/customers/use-customers'
import {AutoWidthTitleInput} from 'src/ui/auto-width-title-input'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

type CustomerDetailsPageTitleProps = {
    customer: CustomerDetail
}

function validateSlug(slug: string) {
    if (!slug.trim()) {
        return 'Slug is required'
    }
    if (!/^[A-Z]{4}$/.test(slug.trim())) {
        return 'Slug must be exactly 4 uppercase letters'
    }
    return null
}

export function CustomerDetailsPageTitle({customer}: CustomerDetailsPageTitleProps) {
    const user = useCurrentUser()
    const canEdit = isAdmin(user)
    const [isEditing, setIsEditing] = useState(false)
    const updateCustomer = useUpdateCustomer(customer.id)

    const form = useForm({
        initialValues: {
            name: customer.name,
            slug: customer.slug,
        },
        validate: {
            name: (value) => (value.trim() ? null : 'Name is required'),
            slug: validateSlug,
        },
    })

    const startEditing = () => {
        form.setValues({name: customer.name, slug: customer.slug})
        form.clearErrors()
        setIsEditing(true)
    }

    const cancelEditing = () => {
        form.setValues({name: customer.name, slug: customer.slug})
        form.clearErrors()
        setIsEditing(false)
    }

    const save = async () => {
        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        try {
            await updateCustomer.mutateAsync({
                name: form.values.name.trim(),
                slug: form.values.slug.trim().toUpperCase() || undefined,
            })
            setIsEditing(false)
        } catch (error) {
            notifications.show({
                title: 'Could not save customer',
                message: error instanceof Error ? error.message : 'Please try again.',
                color: 'red',
            })
        }
    }

    return (
        <Stack gap={4}>
            <DetailPageBackLink to="/customers" listLabel="customers" />

            <Group gap="sm" align="center" wrap="nowrap" style={{minWidth: 0}}>
                {isEditing ? (
                    <AutoWidthTitleInput
                        value={form.values.name}
                        onChange={(event) => form.setFieldValue('name', event.currentTarget.value)}
                        ariaLabel="Customer name"
                    />
                ) : (
                    <Title order={1} style={{minWidth: 0}}>
                        {customer.name}
                    </Title>
                )}

                {!isEditing && canEdit ? (
                    <ActionIcon
                        variant="subtle"
                        size="lg"
                        aria-label="Edit customer name"
                        onClick={startEditing}
                    >
                        <IconEdit size={20} stroke={1.5} />
                    </ActionIcon>
                ) : isEditing ? (
                    <Group gap="xs" wrap="nowrap">
                        <Button variant="default" size="compact-sm" onClick={cancelEditing}>
                            Cancel
                        </Button>
                        <Button
                            size="compact-sm"
                            loading={updateCustomer.isPending}
                            onClick={() => void save()}
                        >
                            Save
                        </Button>
                    </Group>
                ) : null}
            </Group>

            {isEditing ? (
                <TextInput
                    label="Customer slug (4 uppercase letters)"
                    size="sm"
                    maxLength={4}
                    maw={320}
                    {...form.getInputProps('slug')}
                />
            ) : (
                <Text size="sm" c="dimmed">
                    Slug: {customer.slug}
                </Text>
            )}
        </Stack>
    )
}
